import { useMutation, useQuery } from "@apollo/client"
import { Add, Restore, Visibility } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material"
import { format } from "date-fns"
import {
  CREATE_REPORT_VERSION,
  GET_REPORT_VERSIONS,
  RESTORE_REPORT_VERSION,
} from "graphql/reportVersionQueries"
import { ReportVersion } from "graphql/types"
import React, { useState } from "react"

interface ReportVersionDialogProps {
  open: boolean
  onClose: () => void
  reportId: string
  currentVersion: number
  onVersionCreated?: (versionId: string) => void
  onVersionRestored?: (versionId: string) => void
}

export const ReportVersionDialog: React.FC<ReportVersionDialogProps> = ({
  open,
  onClose,
  reportId,
  currentVersion,
  onVersionCreated,
  onVersionRestored,
}) => {
  const [createVersionComment, setCreateVersionComment] = useState("")
  const [showCreateVersion, setShowCreateVersion] = useState(false)

  // Query report versions
  const { data, loading, error, refetch } = useQuery(GET_REPORT_VERSIONS, {
    variables: { reportId },
    skip: !open || !reportId,
  })

  // Mutation to create a new version
  const [createVersion, { loading: creating }] = useMutation(CREATE_REPORT_VERSION, {
    onCompleted: (data) => {
      setCreateVersionComment("")
      setShowCreateVersion(false)
      refetch()
      if (onVersionCreated) {
        onVersionCreated(data.createReportVersion)
      }
    },
    onError: (error) => {
      console.error("Error creating version:", error)
    },
  })

  // Mutation to restore a version
  const [restoreVersion, { loading: restoring }] = useMutation(RESTORE_REPORT_VERSION, {
    onCompleted: (data) => {
      refetch()
      if (onVersionRestored) {
        onVersionRestored(data.restoreReportVersion)
      }
      onClose()
    },
    onError: (error) => {
      console.error("Error restoring version:", error)
    },
  })

  const handleCreateVersion = () => {
    if (reportId) {
      createVersion({
        variables: {
          reportId,
          versionComment: createVersionComment || undefined,
        },
      })
    }
  }

  const handleRestoreVersion = (versionId: string) => {
    if (
      window.confirm(
        "Are you sure you want to restore this version? This will create a new version based on the selected one."
      )
    ) {
      restoreVersion({
        variables: {
          reportId,
          versionId,
        },
      })
    }
  }

  const versions: ReportVersion[] = data?.getReportVersions || []

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Berichtsversionsverwaltung</Typography>
          <Chip label={`Aktuelle Version: ${currentVersion}`} color="primary" variant="outlined" />
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Fehler beim Laden der Versionen: {error.message}
          </Alert>
        )}

        {showCreateVersion && (
          <Box sx={{ mb: 3, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Neue Version erstellen
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Versionskommentar (Optional)"
              value={createVersionComment}
              onChange={(e) => setCreateVersionComment(e.target.value)}
              placeholder="Beschreiben Sie die Änderungen in dieser Version..."
              sx={{ mb: 2 }}
            />
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                onClick={handleCreateVersion}
                disabled={creating}
                startIcon={<Add />}
              >
                Version erstellen
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowCreateVersion(false)
                  setCreateVersionComment("")
                }}
              >
                Abbrechen
              </Button>
            </Box>
          </Box>
        )}

        {!showCreateVersion && (
          <Button
            variant="contained"
            onClick={() => setShowCreateVersion(true)}
            startIcon={<Add />}
            sx={{ mb: 2 }}
          >
            Neue Version erstellen
          </Button>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" gutterBottom>
          Versionsverlauf
        </Typography>

        {loading && <Typography>Versionen werden geladen...</Typography>}

        {!loading && versions.length === 0 && (
          <Typography color="text.secondary">Keine früheren Versionen verfügbar</Typography>
        )}

        <List>
          {versions.map((version) => (
            <ListItem key={version.id} divider>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle2">Version {version.versionNumber}</Typography>
                    {version.status === "DONE" && (
                      <Chip label="Veröffentlicht" size="small" color="success" />
                    )}
                    {version.status === "DRAFT" && (
                      <Chip label="Entwurf" size="small" color="default" />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Erstellt:{" "}
                      {version.createdAt
                        ? format(new Date(version.createdAt), "PPpp")
                        : "Unbekannt"}
                    </Typography>
                    {version.createdBy && (
                      <Typography variant="body2" color="text.secondary">
                        Von: {version.createdBy.displayName || version.createdBy.email}
                      </Typography>
                    )}
                    {version.versionComment && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {version.versionComment}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="view"
                  onClick={() => window.open(`/report/${version.id}`, "_blank")}
                  sx={{ mr: 1 }}
                >
                  <Visibility />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="restore"
                  onClick={() => handleRestoreVersion(version.id)}
                  disabled={restoring}
                >
                  <Restore />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  )
}
